/*
 * STEP 4 — Voice Tributes & Background Music
 * Design: Quiet Reverence
 * Browser-based audio recorder + file upload.
 * Waveform visualizer using CSS animation bars.
 * Supports MP3, WAV, M4A (phone native).
 */

import { ACCEPTED_AUDIO_TYPES, AudioAsset, formatFileSize } from "./intake";
import { Mic, MicOff, Music, Upload, X, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
const nanoid = (n = 10) => Math.random().toString(36).substring(2, 2 + n);
import { toast } from "sonner";

interface Props {
  audio: AudioAsset[];
  onChange: (audio: AudioAsset[]) => void;
}

const MAX_AUDIO = 6;
const WAVEFORM_BARS = 20;

export default function Step4Audio({ audio, onChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [waveHeights] = useState(() =>
    Array.from({ length: WAVEFORM_BARS }, () => Math.random() * 0.7 + 0.3)
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-tribute-${Date.now()}.webm`, { type: 'audio/webm' });
        const newAsset: AudioAsset = {
          id: nanoid(),
          file,
          name: `Voice Tribute ${audio.filter(a => a.type === 'voicemail').length + 1}`,
          durationLabel: formatSecs(recordingSeconds),
          type: 'voicemail',
        };
        onChange([...audio, newAsset]);
        stream.getTracks().forEach(t => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        setRecordingSeconds(0);
        setRecording(false);
      };

      mr.start();
      setRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    } catch {
      toast.error('Microphone access is required to record a voice tribute.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const remaining = MAX_AUDIO - audio.length;
    const toAdd = arr.slice(0, remaining);
    const newAssets: AudioAsset[] = toAdd.map(file => ({
      id: nanoid(),
      file,
      name: file.name.replace(/\.[^/.]+$/, ''),
      durationLabel: '',
      type: file.name.toLowerCase().includes('music') || file.name.toLowerCase().includes('song') ? 'music' : 'voicemail',
    }));
    onChange([...audio, ...newAssets]);
  }, [audio, onChange]);

  const remove = (id: string) => onChange(audio.filter(a => a.id !== id));

  const updateName = (id: string, name: string) =>
    onChange(audio.map(a => a.id === id ? { ...a, name } : a));

  const updateType = (id: string, type: 'voicemail' | 'music') =>
    onChange(audio.map(a => a.id === id ? { ...a, type } : a));

  return (
    <div className="step-enter space-y-6">
      <p className="text-sm text-muted-foreground">
        Record a voice tribute directly in your browser, or upload audio files.
        Voice tributes are woven into the video alongside your photos.
        Background music plays beneath the visuals.
      </p>

      {/* Browser Recorder */}
      <div className="p-5 rounded-xl border-2 border-border bg-card space-y-4">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-primary" />
          <span className="font-['Playfair_Display'] font-600 text-foreground">Record a Voice Tribute</span>
        </div>

        {recording ? (
          <div className="space-y-4">
            {/* Waveform */}
            <div className="flex items-center justify-center gap-1 h-12">
              {waveHeights.map((h, i) => (
                <div
                  key={i}
                  className="waveform-bar"
                  style={{
                    height: `${h * 100}%`,
                    animationDelay: `${i * 0.04}s`,
                    animationDuration: `${0.6 + Math.random() * 0.4}s`,
                  }}
                />
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-primary tabular-nums">{formatSecs(recordingSeconds)}</span>
              <button
                type="button"
                onClick={stopRecording}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-destructive text-destructive-foreground font-medium text-sm hover:opacity-90 transition"
              >
                <Square className="w-4 h-4" />
                Stop Recording
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            disabled={audio.length >= MAX_AUDIO}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition disabled:opacity-40"
          >
            <Mic className="w-4 h-4" />
            Start Recording
          </button>
        )}
      </div>

      {/* File Upload */}
      {audio.length < MAX_AUDIO && (
        <div
          className="upload-frame p-6 flex flex-col items-center gap-3 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Music className="w-5 h-5 text-primary" />
            </div>
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] font-600 text-foreground">Upload Audio Files</p>
            <p className="text-xs text-muted-foreground mt-1">Voicemails, tributes, or background music</p>
            <p className="text-xs text-muted-foreground">MP3, WAV, M4A, AAC</p>
          </div>
          <input
            ref={fileInputRef}
            id="audio-file-input"
            name="audio_files"
            type="file"
            accept={ACCEPTED_AUDIO_TYPES}
            multiple
            className="hidden"
            onChange={e => e.target.files && addFiles(e.target.files)}
          />
        </div>
      )}

      {/* Audio List */}
      {audio.length > 0 && (
        <div className="space-y-3">
          {audio.map(asset => (
            <div key={asset.id} className="thumb-enter flex gap-3 p-3 bg-card rounded-xl border border-border items-start">
              <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                asset.type === 'music' ? 'bg-secondary' : 'bg-primary/10'
              }`}>
                {asset.type === 'music' ? (
                  <Music className="w-5 h-5 text-secondary-foreground" />
                ) : (
                  <Mic className="w-5 h-5 text-primary" />
                )}
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <input
                  id={`audio-name-${asset.id}`}
                  name={`audio_name_${asset.id}`}
                  type="text"
                  value={asset.name}
                  onChange={e => updateName(asset.id, e.target.value)}
                  className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">{asset.file ? formatFileSize(asset.file.size) : asset.r2Key ? '✓ Uploaded' : ''}</span>
                  {asset.durationLabel && <span className="text-xs text-muted-foreground">· {asset.durationLabel}</span>}
                  <div className="flex gap-1 ml-auto">
                    <button
                      type="button"
                      onClick={() => updateType(asset.id, 'voicemail')}
                      className={`text-xs px-2 py-0.5 rounded-full border transition ${
                        asset.type === 'voicemail'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Mic className="w-3 h-3 inline mr-1" />Tribute
                    </button>
                    <button
                      type="button"
                      onClick={() => updateType(asset.id, 'music')}
                      className={`text-xs px-2 py-0.5 rounded-full border transition ${
                        asset.type === 'music'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Music className="w-3 h-3 inline mr-1" />Music
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(asset.id)}
                className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {audio.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground font-['Playfair_Display'] italic">
          A voice tribute transforms a slideshow into a living memory.
        </div>
      )}
    </div>
  );
}

function formatSecs(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}
